import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import React, { useContext, useMemo, useState } from 'react';
import {
    EstimatedTradeCostComparisonColumn,
    estimatedTradeCostComparisonColumnsDefinitionDictionary,
    getEstimatedTradeCostComparisonColDefs,
} from 'widgets/estimatedTradeCost/widget-comparison/components/grid/columns';
import AppContext from 'app/AppContext';

import './EstimatedTradeCostComparisonWidgetGrid.scss';
import benchmarkCellRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/benchmarkCellRenderer';
import profileCellRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/profileCellRenderer';
import {
    constructRows,
    EstimatedTradeCostGridRow,
} from 'widgets/estimatedTradeCost/widget-comparison/components/grid/rows';
import inProgressCellRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/inProgressRenderer';
import failedCellRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/failedRenderer';
import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';
import needsRefreshRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/needsRefreshRenderer';
import zeroQuantityRenderer from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/zeroQuantityRenderer';
import { EstimatedTradeCostComparisonData } from 'widgets/estimatedTradeCost/widget-comparison/estimatedTradeCostComparisonData';

const rowHeight = 25;

export function EstimatedTradeCostComparisonWidgetGrid({
    data,
    height,
    isQuantityDirty,
    zeroQuantity,
    onGridApiSet,
    onClickRefresh,
}: {
    data: EstimatedTradeCostComparisonData | undefined;
    height: number;
    isQuantityDirty: boolean;
    zeroQuantity: boolean;
    onGridApiSet: (gridApi: GridApi) => void;
    onClickRefresh: () => void;
}) {
    const { timeFormat } = useContext(AppContext);
    const [api, setApi] = useState<GridApi>();

    const rows = useMemo(() => {
        const ret = constructRows({
            timeFormat,
            data,
            isQuantityDirty,
            zeroQuantity,
        });
        if (api) {
            api.refreshCells({ force: true });
        }
        return ret;
    }, [api, timeFormat, data, isQuantityDirty, zeroQuantity]);
    const context = useContext(AppContext);

    const colDefs = useMemo(
        () => getEstimatedTradeCostComparisonColDefs(context, onClickRefresh),
        [context, onClickRefresh]
    );

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        EstimatedTradeCostGridRow,
        EstimatedTradeCostComparisonColumn
    >(estimatedTradeCostComparisonColumnsDefinitionDictionary, colDefs);

    const onGridReady = (api: GridApi) => {
        onGridApiSet(api);
        setApi(api);
    };

    const getRowClass = () => 'optimization-row';

    const components = {
        benchmark: benchmarkCellRenderer,
        profile: profileCellRenderer,
        inProgress: inProgressCellRenderer,
        failed: failedCellRenderer,
        needsRefresh: needsRefreshRenderer,
        zeroQuantity: zeroQuantityRenderer,
    };

    return (
        <div
            className="EstimatedTradeCostComparisonWidgetGrid"
            style={{ height }}
        >
            <Grid
                className={
                    'estimated-trade-cost-comparison-grid ag-theme-balham-dark'
                }
                rowHeight={rowHeight}
                headerHeight={rowHeight}
                columnDefs={columnDefs}
                customStyling={true}
                components={components}
                rowData={rows}
                getRowClass={getRowClass}
                getRowDataId={(d: EstimatedTradeCostGridRow) => d.uid}
                suppressColumnVirtualisation={false}
                onGridReady={onGridReady}
                {...gridPropsFromState}
            />
        </div>
    );
}
